/*
 * Copyright (c) 2023-2025 AccelByte Inc. All Rights Reserved
 * This is licensed software from AccelByte Inc, for limitations
 * and restrictions contact your company contract manager.
 */

import { AccelbyteSDK, ApiArgs, ApiUtils, Network, SDKRequestConfig } from '@accelbyte/sdk'
import { CustomerPortalSDK, createCustomerPortalSDK } from '@cp/sdk'
import { create } from 'zustand'
import { Env } from '~/constants/env-vars'
import { MOCK_PATH, PROXY_PATH } from '~/constants/paths'
import { SHARED_SDK_OPTIONS } from '~/constants/sdk'

interface SdkState {
  sdk: CustomerPortalSDK
}

const IS_LOCAL_AND_NOT_TEST = (Env.IS_DEV || Env.IS_USE_PROXY_IN_DOCKER) && !Env.IS_TESTING

// For testing and production, we always use `BASE_URL`.
// For local, we want to proxy to the base path `/web/proxy`, so we don't use absolute origin `BASE_URL`.
const BROWSER_BASE_URL = IS_LOCAL_AND_NOT_TEST ? PROXY_PATH : Env.BASE_URL
const BROWSER_PROXY_BASE_URL = IS_LOCAL_AND_NOT_TEST ? PROXY_PATH : `${Env.BASE_URL}${PROXY_PATH}`

// TODO: remove this and we can move this file as something like `constants/browser-sdk.ts`.
export const useSdk = create<SdkState>(() => ({
  sdk: createCustomerPortalSDK({
    options: { baseURL: PROXY_PATH, ...SHARED_SDK_OPTIONS }
  })
}))

// The rule of thumb is as follows:
// 1. Redirect to login website: use `BROWSER_IC_BASE_URL_SDK`
// 2. Everything else to IC service (through Customer Portal): via proxy `BROWSER_SDK_WITH_PROXY`
// 3. Everything else to services on the same domain: direct calls (no proxy needed): `BROWSER_SDK`
export const BROWSER_SDK = createCustomerPortalSDK({
  // For local, we still need proxy because of CORS.
  options: { baseURL: BROWSER_BASE_URL, ...SHARED_SDK_OPTIONS }
})

export const BROWSER_SDK_WITH_PROXY = createCustomerPortalSDK({
  options: { baseURL: BROWSER_PROXY_BASE_URL, ...SHARED_SDK_OPTIONS }
})

export const BROWSER_IC_BASE_URL_SDK = createCustomerPortalSDK({
  options: { baseURL: Env.IC_HTTPS_BASE_URL, ...SHARED_SDK_OPTIONS }
})

export const MOCK_SDK = createCustomerPortalSDK({
  options: { baseURL: MOCK_PATH, ...SHARED_SDK_OPTIONS }
})

export const createExtensionBrowserSDK = (extensionKey: string, headerSettings?: SDKRequestConfig['headers']) => {
  return createCustomerPortalSDK({
    // For local, we still need proxy because of CORS.
    options: { baseURL: `${BROWSER_BASE_URL}/cp-server`, ...SHARED_SDK_OPTIONS },
    config: {
      withCredentials: true,
      headers: {
        'extension-key': extensionKey,
        ...headerSettings
      }
    }
  })
}

export const createBrowserNetworkSDK = (sdk: AccelbyteSDK, args?: ApiArgs) => {
  const sdkAssembly = sdk.assembly()
  const requestConfig = ApiUtils.mergedConfigs(sdkAssembly.config, args)

  return Network.create(requestConfig)
}

/**
 * Only supposed to be used against Customer Database Email Proxy endpoints
 */
export const BROWSER_NETWORK_SDK = createBrowserNetworkSDK(BROWSER_SDK)
